import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { getAllKycSubmissions, approveKyc, rejectKyc } from "@/services/supabaseService";
import type { KycSubmission } from "@/lib/supabase";
import AdminLayout from "@/components/feature/AdminLayout";

export default function KycAudit() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [submissions, setSubmissions] = useState<KycSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"" | "success" | "error">("");
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("pending");
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);

  const loadData = async () => {
    setIsLoading(true);
    setMsg("");
    try {
      const data = await getAllKycSubmissions();
      setSubmissions(data);
      if (data.length === 0) {
        setMsg("目前暫無實名認證申請記錄");
        setMsgType("");
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "載入失敗，請重新整理頁面再試";
      setMsg(message);
      setMsgType("error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-forbid-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">權限不足</p>
          <p className="text-sm text-gray-400 mb-6">只有管理員才能訪問此頁面</p>
          <button onClick={() => navigate("/")} className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium cursor-pointer">
            返回首頁
          </button>
        </div>
      </div>
    );
  }

  const filtered = filter === "all"
    ? submissions
    : submissions.filter((k) =>
        filter === "pending"
          ? k.status === "待審核"
          : filter === "approved"
          ? k.status === "已通過"
          : k.status === "已拒絕"
      );

  const pendingCount = submissions.filter((k) => k.status === "待審核").length;
  const approvedCount = submissions.filter((k) => k.status === "已通過").length;
  const rejectedCount = submissions.filter((k) => k.status === "已拒絕").length;

  const handleApprove = async (id: string) => {
    try {
      await approveKyc(id);
      setMsg(`實名認證 ${id} 已通過，用戶解鎖提現功能`);
      setMsgType("success");
      await loadData();
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : "操作失敗");
      setMsgType("error");
    }
    setTimeout(() => setMsg(""), 4000);
  };

  const handleReject = async () => {
    if (!rejectId || !rejectNote.trim()) return;
    try {
      await rejectKyc(rejectId, rejectNote.trim());
      setMsg(`實名認證 ${rejectId} 已拒絕，用戶可重新提交`);
      setMsgType("success");
      await loadData();
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : "操作失敗");
      setMsgType("error");
    }
    setRejectId(null);
    setRejectNote("");
    setTimeout(() => setMsg(""), 4000);
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <button onClick={() => navigate("/admin/dashboard")} className="hover:text-amber-600 cursor-pointer">
            管理後台
          </button>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-xs"></i>
          </div>
          <span className="text-gray-700">實名審核</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "全部申請", value: submissions.length, color: "bg-gray-100 text-gray-700", icon: "ri-file-list-3-line" },
            { label: "待審核", value: pendingCount, color: "bg-amber-100 text-amber-700", icon: "ri-time-line" },
            { label: "已通過", value: approvedCount, color: "bg-emerald-100 text-emerald-700", icon: "ri-check-line" },
            { label: "已拒絕", value: rejectedCount, color: "bg-red-100 text-red-700", icon: "ri-close-line" },
          ].map((s) => (
            <div key={s.label} className={`rounded-xl p-4 text-center shadow-sm ${s.color}`}>
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <i className={`${s.icon} text-xs`}></i>
                <p className="text-xs font-medium">{s.label}</p>
              </div>
              <p className="text-xl font-bold">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 inline-flex">
          {([
            { key: "pending" as const, label: "待審核" },
            { key: "all" as const, label: "全部" },
            { key: "approved" as const, label: "已通過" },
            { key: "rejected" as const, label: "已拒絕" },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                filter === tab.key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Message */}
        {msg && (
          <div className={`rounded-lg px-4 py-3 text-sm border mb-1 flex items-center gap-2 shadow-sm ${
            msgType === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-600"
              : "bg-red-50 border-red-200 text-red-600"
          }`}>
            <div className="w-4 h-4 flex items-center justify-center shrink-0">
              <i className={`${msgType === "success" ? "ri-check-line" : "ri-error-warning-line"} text-xs`}></i>
            </div>
            {msg}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="px-6 py-12 text-center text-gray-400 text-sm">載入中...</div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-400 text-sm">暫無實名認證申請</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-50">
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">申請編號</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">用戶</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">實名資訊</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">證件照片</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">申請時間</th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-400">狀態</th>
                    <th className="px-5 py-3 text-right text-xs font-medium text-gray-400">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((k) => (
                    <tr key={k.id} className="hover:bg-gray-50/50">
                      <td className="px-5 py-4">
                        <p className="text-sm font-medium text-gray-800">{k.id}</p>
                        {k.admin_note && <p className="text-xs text-red-500 mt-0.5">備註：{k.admin_note}</p>}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700">
                        <p>{k.user_name}</p>
                        <p className="text-xs text-gray-400">{k.user_email}</p>
                      </td>
                      <td className="px-5 py-4 text-xs text-gray-500 leading-relaxed">
                        <p>姓名：{k.real_name}</p>
                        <p>證號：{k.id_number}</p>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPreviewPhoto(k.front_photo)}
                            className="w-16 h-10 rounded overflow-hidden border border-gray-200 hover:border-amber-400 transition-colors cursor-pointer relative"
                          >
                            <img
                              src={k.front_photo}
                              alt="正面"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = "none";
                                const parent = (e.target as HTMLImageElement).parentElement;
                                if (parent) {
                                  parent.classList.add("flex", "items-center", "justify-center", "bg-gray-100");
                                  parent.innerHTML = `<div class="w-full h-full flex items-center justify-center bg-gray-100"><i class="ri-image-line text-gray-400 text-xs"></i></div>`;
                                }
                              }}
                            />
                          </button>
                          <button
                            onClick={() => setPreviewPhoto(k.back_photo)}
                            className="w-16 h-10 rounded overflow-hidden border border-gray-200 hover:border-amber-400 transition-colors cursor-pointer relative"
                          >
                            <img
                              src={k.back_photo}
                              alt="反面"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = "none";
                                const parent = (e.target as HTMLImageElement).parentElement;
                                if (parent) {
                                  parent.classList.add("flex", "items-center", "justify-center", "bg-gray-100");
                                  parent.innerHTML = `<div class="w-full h-full flex items-center justify-center bg-gray-100"><i class="ri-image-line text-gray-400 text-xs"></i></div>`;
                                }
                              }}
                            />
                          </button>
                          {k.holding_photo && (
                            <button
                              onClick={() => setPreviewPhoto(k.holding_photo!)}
                              className="w-16 h-10 rounded overflow-hidden border border-gray-200 hover:border-amber-400 transition-colors cursor-pointer relative"
                            >
                              <img
                                src={k.holding_photo}
                                alt="手持"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                  const parent = (e.target as HTMLImageElement).parentElement;
                                  if (parent) {
                                    parent.classList.add("flex", "items-center", "justify-center", "bg-gray-100");
                                    parent.innerHTML = `<div class="w-full h-full flex items-center justify-center bg-gray-100"><i class="ri-image-line text-gray-400 text-xs"></i></div>`;
                                  }
                                }}
                              />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-xs text-gray-400">
                        {new Date(k.submitted_at).toLocaleString("zh-TW")}
                        {k.reviewed_at && <p className="text-emerald-600 mt-0.5">審核 {new Date(k.reviewed_at).toLocaleString("zh-TW")}</p>}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          k.status === "待審核"
                            ? "bg-amber-100 text-amber-700"
                            : k.status === "已通過"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-600"
                        }`}>
                          {k.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {k.status === "待審核" ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleApprove(k.id)}
                              className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 cursor-pointer whitespace-nowrap"
                            >
                              通過
                            </button>
                            <button
                              onClick={() => { setRejectId(k.id); setRejectNote(""); setMsg(""); }}
                              className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 cursor-pointer whitespace-nowrap"
                            >
                              拒絕
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">已處理</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Photo Preview Modal */}
      {previewPhoto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4" onClick={() => setPreviewPhoto(null)}>
          <div className="relative max-w-3xl max-h-[80vh] w-full">
            <img
              src={previewPhoto}
              alt="證件預覽"
              className="max-w-full max-h-[80vh] rounded-lg object-contain mx-auto"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
                const parent = (e.target as HTMLImageElement).parentElement;
                if (parent) {
                  parent.innerHTML = `
                    <div class="bg-gray-800 rounded-lg p-8 text-center max-w-md mx-auto">
                      <div class="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <i class="ri-image-line text-gray-500 text-3xl"></i>
                      </div>
                      <p class="text-gray-400 text-sm mb-2">圖片無法讀取</p>
                      <p class="text-gray-500 text-xs mb-4">可能的原因：圖片連結已失效，或圖片尚未正確上傳</p>
                      <button class="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 text-sm hover:bg-gray-600 cursor-pointer" onclick="this.closest('.fixed').click()">
                        關閉
                      </button>
                    </div>
                  `;
                }
              }}
            />
            <button
              onClick={() => setPreviewPhoto(null)}
              className="absolute -top-10 right-0 w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center hover:bg-white/40 cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-close-line"></i>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-base font-semibold text-gray-900 mb-1">拒絕實名認證</h3>
            <p className="text-sm text-gray-500 mb-4">申請編號：{rejectId}</p>
            <p className="text-sm text-gray-500 mb-4">拒絕後用戶可重新提交申請。請輸入原因：</p>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="例如：照片模糊、姓名不符、證件過期..."
              maxLength={200}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 mb-4 resize-none"
            />
            <p className="text-xs text-gray-400 mb-4 text-right">{rejectNote.length}/200</p>
            <div className="flex gap-2">
              <button
                onClick={handleReject}
                disabled={!rejectNote.trim()}
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                確認拒絕
              </button>
              <button
                onClick={() => { setRejectId(null); setRejectNote(""); }}
                className="flex-1 py-2.5 rounded-lg bg-gray-100 text-gray-600 text-sm hover:bg-gray-200 cursor-pointer"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}